// Eén kaart in het raster: vierkante foto, favoriet-badge, titel, prijs of potjestekst
// en een ronde wieg-knop rechtsonder. Alle tekst gaat via textContent.
import type { GuestGift } from '../shared/api-types.ts';
import { withLine, withoutGift } from './cart.ts';
import { openPotAmount, picFor } from './cart-dialogs.ts';
import { formatEuro, h } from './dom.ts';
import { icon } from './icons.ts';
import { isGiven } from './list-filter.ts';
import { cartLineFor, currentCart, setCart } from './store.ts';

function cribButton(g: GuestGift): HTMLButtonElement {
  const inCart = cartLineFor(g.id) !== undefined;
  const btn = h('button', {
    type: 'button', class: inCart ? 'cribbtn in' : 'cribbtn',
    'aria-label': inCart ? `Haal ${g.title} uit je wiegje` : `Leg ${g.title} in je wiegje`, 'aria-pressed': String(inCart),
    title: inCart ? 'In je wiegje' : 'Leg in je wiegje',
  }, icon(inCart ? 'check' : 'cribPlus'));
  btn.dataset.fk = `crib-${g.id}`;
  btn.addEventListener('click', () => {
    if (g.kind === 'potje') openPotAmount(g, false);
    else if (inCart) setCart(withoutGift(currentCart(), g.id), `${g.title} uit je wiegje gehaald.`);
    else setCart(withLine(currentCart(), { giftId: g.id, amountCents: g.priceCents }), `${g.title} ligt in je wiegje.`);
  });
  return btn;
}

function givenTag(g: GuestGift): HTMLElement {
  if (g.kind === 'potje') return h('span', { class: 'tag ok', text: 'Volledig bijeengelegd' });
  return h('span', { class: 'tag ok', text: g.thanks ? `Bedankt ${g.thanks}!` : 'Geschonken' });
}

function progress(target: number, left: number): HTMLElement {
  const fill = h('span');
  fill.style.width = `${Math.min(100, Math.round(((target - left) / target) * 100))}%`; // CSSOM, geen style-attribuut
  // Decoratief: de tekst "Nog X nodig" is de toegankelijke waarde. Geen gegeven bedrag in aria.
  return h('div', { class: 'bar', 'aria-hidden': 'true' }, fill);
}

function priceBlock(g: GuestGift): HTMLElement {
  const line = cartLineFor(g.id);
  const status = line ? h('span', { class: 'incart', text: g.kind === 'potje' ? `In je wiegje: ${formatEuro(line.amountCents)}` : 'In je wiegje' }) : null;
  if (g.kind === 'cadeau') return h('div', { class: 'pricebox' }, h('span', { class: 'price', text: formatEuro(g.priceCents) }), status);
  if (!g.priceCents) return h('div', { class: 'pricebox' }, h('span', { class: 'potline', text: 'Geef wat je wil' }), status);
  const left = g.remainingCents ?? 0;
  return h('div', { class: 'pricebox' },
    h('span', { class: 'potline' }, h('b', { text: left > 0 ? `Nog ${formatEuro(left)} nodig` : `Doel van ${formatEuro(g.priceCents)} bereikt` })),
    left > 0 ? h('span', { class: 'goal', text: `doel ${formatEuro(g.priceCents)}` }) : null,
    progress(g.priceCents, left),
    status);
}

export function giftCard(g: GuestGift): HTMLElement {
  const given = isGiven(g);
  const shop = /^https?:\/\//i.test(g.url)
    ? h('a', { class: 'shop', href: g.url, target: '_blank', rel: 'noopener noreferrer', text: 'Bekijk in de winkel' })
    : null;
  return h('li', { class: given ? 'card done' : 'card' },
    h('div', { class: 'media' },
      picFor(g, 'pic', ''),
      g.favorite ? h('span', { class: 'badge', title: 'Favoriet van de ouders', 'aria-label': 'Favoriet van de ouders' }, icon('heart'), h('span', { class: 'long', text: 'Favoriet van de ouders' }), h('span', { class: 'short', text: 'Favoriet' })) : null),
    h('div', { class: 'body' },
      h('h3', { class: 'title', text: g.title }),
      g.note ? h('p', { class: 'note', text: g.note }) : null,
      shop,
      h('div', { class: 'foot' },
        priceBlock(g),
        given ? givenTag(g) : cribButton(g))));
}
